import { z } from "zod";
import { catalogueLocale } from "../catalog/vocabulary";
import { equipmentPlace } from "./equipment";
import { bodyRegion, injuryOccurrence } from "./injuries";
/**
 * The change log — *"what was done to my account, and by whom"*.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── THIS IS A COUNTERWEIGHT, NOT A FEATURE ──────────────────────────────────
 * `D65` makes consent broad: once somebody accepts being managed by a group,
 * that group's trainers write their training data and there is no per-change
 * approval. That is the professional frame and it is right — you consent to
 * being someone's client, you do not approve each note they write.
 *
 * Broad consent is only legitimate with two counterweights, and `D65` states
 * both as REQUIREMENTS: the person can always see what was done and by whom,
 * and the person can always leave, alone and immediately
 * (`leave-group.ts`). This shape is the first of the two.
 *
 * The consequence for anyone editing this file: **a change the trainer made
 * that this shape cannot carry is the failure mode.** Adding a new attributed
 * table without adding it to {@link changeEntity} does not break a test in the
 * contract package — it quietly shrinks the answer.
 *
 * ── AND IT IS DELIBERATELY NOT AN AUDIT LOG ─────────────────────────────────
 * `D61` refused the audit module outright. This answers ONE question for ONE
 * person about their OWN account, out of columns `D69` already stores — no
 * table, no retention policy, no administrator filters. The moment it grows a
 * `subjectAccountId` parameter or an actor filter it has become the module
 * `D61` refused, with a table behind it.
 * ═══════════════════════════════════════════════════════════════════════════
 */
/**
 * Which of the attributed tables the entry came from.
 *
 * These are exactly the tables that carry `actor_account` and `actor_path`
 * (`D69`). `training_plan_item` is absent on purpose: it has no actor columns
 * of its own, and an item is only ever written through its plan, whose row
 * carries the attribution and whose `updated_at` moves when an item is added,
 * reordered or removed.
 *
 * ── THE LAST FIVE ARE `D114`/`D115`, AND THEY CLOSE A GAP RATHER THAN ADD ONE
 * The onboarding writes a profile, a training config, an equipment list, an
 * injury report and a PAR-Q — five kinds of write about a person's own body
 * and constraints, every one of which a trainer may also make. Before `0006`
 * they had no actor columns and this list had four members, which is exactly
 * why `write-tools.ts:56-67` WITHHELD `updateProfile` from the agent: a write
 * the change feed cannot see is a write the person cannot audit, and `D65`'s
 * broad consent is only legitimate while they can. The gap closes here.
 *
 * Appended rather than sorted in: nothing reads this list positionally today,
 * and keeping the original four first makes the diff say what changed.
 */
export const CHANGE_ENTITIES = [
    "measurement",
    "plan",
    "session",
    "set",
    "profile",
    "training_config",
    "equipment",
    "injury",
    "parq",
];
export const changeEntity = z.enum(CHANGE_ENTITIES);
/**
 * `D69`'s second column, on the wire.
 *
 * Kept as its own field beside {@link attribution} rather than folded into it:
 * the derived word is what a screen renders, and this is the fact the database
 * holds. A client that wants to say something the four words below do not say
 * still has the raw pair.
 */
export const changeActorPath = z.enum(["direct", "agent"]);
/**
 * ── THE THREE SHAPES `D69` STORED TWO COLUMNS TO TELL APART ─────────────────
 *
 * | value                  | what a screen says                          |
 * |------------------------|---------------------------------------------|
 * | `self`                 | *"you"*                                     |
 * | `self_via_agent`       | *"you, through the assistant"*              |
 * | `other`                | *"your trainer"*                            |
 * | `other_via_agent`      | *"your trainer, through the assistant"*     |
 *
 * The last row is the one `D69` exists for. *"The agent, at your trainer's
 * request"* is a different answer from *"your trainer"* in a dispute, because
 * it means a model composed the row and a human at most did not disagree —
 * and collapsing the pair into one string would make that a parsing problem.
 *
 * There are FOUR values and not three because `self_via_agent` is real: the
 * student who tells the assistant to log a set caused that row through the
 * agent, and rendering it as *"you"* would claim they typed it. The task names
 * three because three is the number that matters for the trainer relationship;
 * the fourth costs one word and its absence would be a lie.
 *
 * `self` is decided against the SUBJECT of the feed, which — since only the
 * account itself may read it — is also the reader.
 */
export const CHANGE_ATTRIBUTIONS = [
    "self",
    "self_via_agent",
    "other",
    "other_via_agent",
];
export const changeAttribution = z.enum(CHANGE_ATTRIBUTIONS);
/**
 * What every entry carries, whatever it is about.
 *
 * ── `at` IS THE EVENT'S OWN CLOCK, NOT A WRITE TIMESTAMP ────────────────────
 * `measured_at`, `updated_at`, `started_at`, `performed_at` — whichever the
 * row has. None of these four tables has a `created_at`, and inventing one is
 * a migration; the change feed is a read over columns that already exist. The practical
 * difference is small and it is stated here rather than discovered: a weigh-in
 * entered three days late sorts to the day it happened, not the day it was
 * typed, which is what somebody scanning their own history expects to see.
 *
 * ── `removedAt` IS SET, AND THE ROW IS STILL HERE (`D53`) ───────────────────
 * A change that was later undone is part of the answer to *"what was done to
 * my account"*. Filtering soft-deleted rows out is correct for every other
 * read in the system and wrong for this one.
 *
 * **Who removed it is not recorded, anywhere.** The soft delete writes
 * `deleted_at` and nothing else — deliberately, because an UPDATE that could
 * move the actor columns would let a row's attribution be rewritten after the
 * fact. So `removedAt` says WHEN and the entry's own attribution stays that of
 * the write. In the common case that is enough: a correction is a NEW row plus
 * a soft delete of the old one, so the corrector appears as the new row's
 * actor. A `deleted_by` column is the honest fix and it is a migration, not
 * this task.
 */
const changeBase = {
    /** Who caused the write (`D69`). An account id, raw — the feed does not
     *  resolve names, because a name is a read into another module. */
    actorAccount: z.string().min(1),
    actorPath: changeActorPath,
    /** See the docblock: the event's own clock, ISO-8601. */
    at: z.string().datetime(),
    attribution: changeAttribution,
    /** The row's own id, so a screen can link through to what changed. */
    entityId: z.string().min(1),
    /** Set when the row was later soft-deleted. The entry stays. */
    removedAt: z.string().datetime().nullable(),
};
/** A weigh-in, a body-fat reading, a circumference (`D68`). */
export const measurementChange = z.object({
    ...changeBase,
    entity: z.literal("measurement"),
    kind: z.string().min(1),
    unit: z.string().min(1),
    value: z.number(),
});
/** The three kinds of item-level plan line (T35, plan D37). There is no
 *  `changed`: a line whose exercise stayed and whose targets moved is not
 *  "which exercise went out and which came in". */
export const PLAN_ITEM_CHANGE_KINDS = ["added", "removed", "swapped"];
export const planItemChangeKind = z.enum(PLAN_ITEM_CHANGE_KINDS);
/** One side of a line: the exercise, named in the request's `locale`. */
export const planItemChangeExercise = z.object({
    exerciseId: z.string().min(1),
    name: z.string().min(1),
});
/**
 * One item-level line under a plan entry — *"Trocou Crucifixo por Supino
 * inclinado"* (T35, plan D37).
 *
 * `swapped` has both sides, `added` only `after`, `removed` only `before`.
 * The actor ("você" / "personal" / the engine) is the ENTRY's own actor
 * fields: an item has no actor columns, so the plan row's last writer (`D69`)
 * is the only attribution there is, and two people editing one plan within
 * one entry read as the last writer.
 */
export const planItemChange = z.object({
    kind: planItemChangeKind,
    sessionLabel: z.string().min(1),
    before: planItemChangeExercise.nullable(),
    after: planItemChangeExercise.nullable(),
});
/** The plan itself — created, published, archived, or its items rewritten. */
export const planChange = z.object({
    ...changeBase,
    entity: z.literal("plan"),
    /** NULL for a self-directed plan; set to the group that holds the pen
     *  (`D51`). Present because *"which of my trainers wrote this"* is exactly
     *  the question this feed exists to answer. */
    groupId: z.string().nullable(),
    status: z.string().min(1),
    /**
     * Which exercise went out and which came in (T35, plan D37). Newest first,
     * at most 20 per entry. Two sources: the publish transition — the plan
     * against the one in effect just before it, as both stood at the publish —
     * and in-place edits after publication, where a removal and an addition in
     * one session at one instant are one `swapped`. Empty for a plan never
     * published, and no publish transition is reported when the predecessor
     * cannot be recovered. OPTIONAL on the wire: a server older than T35 never
     * sends it. The server's docblock is
     * `packages/modules/training/src/core/model/plan-item-history.ts`.
     */
    items: z.array(planItemChange).optional(),
});
/** One bout of training. */
export const sessionChange = z.object({
    ...changeBase,
    /** NULL while the session is still open. */
    endedAt: z.string().datetime().nullable(),
    entity: z.literal("session"),
});
/** One logged set — the most-written row in the system (`D69`). */
export const setChange = z.object({
    ...changeBase,
    entity: z.literal("set"),
    exerciseId: z.string().min(1),
    /** All three nullable: the catalogue decides which are meaningful for a
     *  given exercise, and a plank has no reps. */
    load: z.number().nullable(),
    reps: z.number().nullable(),
    rpe: z.number().nullable(),
    sessionId: z.string().min(1),
    setNumber: z.number(),
});
/**
 * The athlete profile — date of birth, sex, height (`D114`).
 *
 * `entityId` is the ACCOUNT id: `athlete_profile` is keyed by account and has
 * no id of its own. Which of the three fields moved is not carried, because
 * the row is rewritten as a whole and the feed answers *"what was done"* and
 * not *"which column"* — the same reason `planChange` carries a status and
 * not a diff.
 */
export const profileChange = z.object({
    ...changeBase,
    entity: z.literal("profile"),
});
/** Goal, days, session length, experience (`D115`). Keyed by account, like the
 *  profile, so `entityId` is the account id again. */
export const trainingConfigChange = z.object({
    ...changeBase,
    entity: z.literal("training_config"),
    /** The derived level after the write. It is what changes what the engine
     *  proposes, so it is the one field worth showing on the row. */
    level: z.string().min(1),
});
/**
 * The availability checklist AND where the person trains, replaced as a whole.
 * `entityId` is the account.
 *
 * One entry and not two: `athlete_equipment` and `athlete_equipment_place` are
 * written by the same `PUT /me/equipment`, in one batch with one actor pair.
 * `place` is a field on THIS entity rather than a tenth `entity` literal for
 * exactly the reason {@link changeLogEntry} is a discriminated union — a new
 * literal makes every already-installed app fail to parse the whole page,
 * while a new optional field is additive.
 */
export const equipmentChange = z.object({
    ...changeBase,
    entity: z.literal("equipment"),
    /** How many pieces were available after the write. A count and not the list:
     *  the feed is a history, and 31 ids per row is a payload nobody reads. */
    availableCount: z.number(),
    /**
     * Where the person trains after the write (`D41`), `null` when never said or
     * cleared.
     *
     * OPTIONAL, and absent is not `null`: it means the server is older than
     * this field, never that no place is set (`D172`,
     * `backward-compat.test.ts`). An app built after this change still has to
     * parse a server that has not deployed, or was rolled back — and a screen
     * that must tell the two apart reads `undefined` against `null` itself.
     */
    place: equipmentPlace.nullable().optional(),
});
/**
 * One injury or pain report. The only one of the five with a row id of its
 * own, because injuries are kept as a history (`D115` — soft delete where a
 * history matters) rather than replaced in place.
 */
export const injuryChange = z.object({
    ...changeBase,
    entity: z.literal("injury"),
    occurrence: injuryOccurrence,
    region: bodyRegion,
});
/** One PAR-Q submission (`D98`). */
export const parqChange = z.object({
    ...changeBase,
    entity: z.literal("parq"),
    /** How many of the seven were `true`. The answers themselves are a medical
     *  disclosure and do not belong in a scrollable feed. */
    positiveCount: z.number(),
});
/**
 * Discriminated on `entity`, so a client that handles eight of the nine fails
 * to compile rather than rendering an empty row.
 */
export const changeLogEntry = z.discriminatedUnion("entity", [
    measurementChange,
    planChange,
    sessionChange,
    setChange,
    profileChange,
    trainingConfigChange,
    equipmentChange,
    injuryChange,
    parqChange,
]);
/** The default page, and what the screen asks for. */
export const DEFAULT_CHANGE_LOG_LIMIT = 50;
/**
 * The largest page anyone may ask for. D1 bills ROWS READ (`T-6`) and this
 * read fans out over four tables, so the cap is a cost control multiplied by
 * four rather than a politeness.
 */
export const MAX_CHANGE_LOG_LIMIT = 200;
/**
 * Query strings arrive as strings or not at all, so both fields are decoded
 * from one. `z.coerce` is deliberately not used anywhere in this package: it
 * turns `""` into `0` and `"abc"` into `NaN`, and a limit that silently became
 * zero returns an empty feed that reads as *"nothing was ever done to your
 * account"* — the single most misleading answer this endpoint can give.
 */
export const changeLogQuery = z.object({
    /**
     * Keyset pagination, exclusive: *"the page after this row"*. An OPAQUE
     * string — the client echoes back the `nextBefore` it was given and never
     * looks inside.
     *
     * It is not a timestamp, and it must not become one. The feed merges four
     * tables, so the sort key is `(at, entityId)`: an exclusive cursor on the
     * instant alone silently drops every entry sharing the boundary instant,
     * which in the one endpoint whose correctness property is completeness is
     * the worst bug available. It is encoded so it does not LOOK constructible —
     * see `core/pagination/cursor.ts` in `@repo/training`, which owns the format
     * and refuses a cursor it cannot read rather than restarting.
     *
     * An offset would be wrong rather than merely slower: a row written between
     * two requests shifts every offset after it, which duplicates or skips an
     * entry silently.
     */
    before: z.string().min(1).optional(),
    /** The language a plan entry's `items` name their exercises in (T35).
     *  English when absent, the catalogue's base language. */
    locale: catalogueLocale.optional(),
    limit: z
        .string()
        .regex(/^\d+$/, "expected a positive integer")
        .transform(Number)
        .pipe(z.int().positive().max(MAX_CHANGE_LOG_LIMIT))
        .optional(),
});
export const changeLogOutput = z.object({
    /** Newest first. Empty when nothing has been done, which for a brand-new
     *  account is the truthful answer and not an error. */
    entries: z.array(changeLogEntry),
    /**
     * The opaque `before` for the next page, or NULL at the end of the history.
     *
     * Returned rather than left to the client to compute from `entries.at(-1)`:
     * the server is the one that knows whether it truncated, and a client that
     * derived the cursor itself would page forever over a full last page — and
     * could not derive it anyway, since the key is a pair and the encoding is
     * deliberately not part of the contract.
     */
    nextBefore: z.string().min(1).nullable(),
});
//# sourceMappingURL=change-log.js.map